/**
 * @expFlag promo-in-webview-public
 * @ticket CLOUDWEB-20374
 * @file
 */
import React, { useCallback, useEffect, useState } from 'react';
import { abPromoInWebviewPublic, PROMO_ID } from 'reactApp/appHelpers/featuresHelpers/features/promoInWebviewPublic';
import { sendRedirectPromoElementAnalytics } from 'reactApp/modules/redirectToApp/redirectToApp.analytics';
import { ERedirectPromoElement } from 'reactApp/modules/redirectToApp/redirectToApp.constants';
import { canShowPromo, recordPromoShown } from 'reactApp/modules/redirectToApp/redirectToApp.helpers';
import { PromoBlock } from 'reactApp/ui/Mobile/PromoBlock/PromoBlock';
import { PromoBlockData } from 'reactApp/ui/Mobile/PromoBlock/PromoBlock.data';
import opener from 'reactApp/utils/opener';

interface ShowData {
    onShow(): void;
    onClick(): void;
    onSecondaryClick?(): void;
    onClose?(): void;
    doNotClose?: boolean;
}

const STORAGE_KEY = `${PROMO_ID}-last-shown`;

export const useRedirectToAppPromoBlock = () => {
    const [showData, setShowData] = useState<null | ShowData>(null);

    const showBlock = abPromoInWebviewPublic && abPromoInWebviewPublic.type === 'b' && canShowPromo(STORAGE_KEY);
    const deepLink = `?mt_deeplink=${window.location.href}`;

    const promoData: ShowData = {
        onShow: () => {},
        onClick: () => {
            recordPromoShown(STORAGE_KEY);
            if (abPromoInWebviewPublic) {
                sendRedirectPromoElementAnalytics({
                    trkLink: abPromoInWebviewPublic.href,
                    promoElement: ERedirectPromoElement.promoBlock,
                    action: 'click',
                });
                opener(abPromoInWebviewPublic.href + deepLink);
            }
        },
        onClose: () => {
            recordPromoShown(STORAGE_KEY);
            if (abPromoInWebviewPublic) {
                sendRedirectPromoElementAnalytics({
                    trkLink: abPromoInWebviewPublic.href,
                    promoElement: ERedirectPromoElement.promoBlock,
                    action: 'close',
                });
            }
        },
    };
    const promoBlockProps = PromoBlockData.REDIRECT_TO_APP;

    const handleClick = useCallback(() => {
        showData?.onClick();
        if (!showData?.doNotClose) {
            setShowData(null);
        }
    }, [showData]);

    const handleSecondaryClick = useCallback(() => {
        showData?.onSecondaryClick?.();
        if (!showData?.doNotClose) {
            setShowData(null);
        }
    }, [showData]);

    const handleShow = useCallback(() => {
        showData?.onShow();
    }, [showData]);

    const handleClose = useCallback(() => {
        showData?.onClose?.();
        setShowData(null);
    }, [showData]);

    useEffect(() => {
        if (!showBlock) {
            return;
        }

        if (abPromoInWebviewPublic) {
            sendRedirectPromoElementAnalytics({
                trkLink: abPromoInWebviewPublic.href,
                promoElement: ERedirectPromoElement.promoBlock,
                action: 'show',
            });
        }

        setShowData(promoData);
    }, [showBlock]);

    if (!showData || !promoBlockProps) {
        return null;
    }

    return (
        <PromoBlock
            {...promoBlockProps}
            onClick={handleClick}
            onClose={handleClose}
            onShow={handleShow}
            onSecondaryClick={handleSecondaryClick}
            showOnce={false}
        />
    );
};
